import { css } from '@emotion/react';
import { formatPhoneNumber } from '@flysas-tech/book-value-stream-helpers';
import { CepId, Traveler, TRAVELER_TYPE } from '@flysas-tech/book-value-stream-models';
import { BREAKPOINTS, Divider, Heading, tokens } from '@flysas-tech/commercial-ui';
import { Option } from 'models';
import { forwardRef, useState } from 'react';
import {
  getDateOfBirthError,
  getEmailError,
  getFirstNameError,
  getLastNameError,
  PassengerErrors,
  PassengerFieldError,
  PassengerKey,
  SavedTraveler,
} from 'shared';

import { FieldsetGroup, getErrorMessage } from '../..';
import { Card } from '../Card';

import { FrequentTravelerForm } from './FrequentTravelerForm';
import { InfantSeatingNotice } from './InfantSeatingNotice';
import {
  DateOfBirthInput,
  EmailInput,
  FirstNameInput,
  FrequentFlyerToggle,
  GenderInput,
  JoinEurobonusInput,
  LastNameInput,
  PhoneInput,
} from './InputFields';
import { SavedTravelers } from './SavedTravelers';

const styles = {
  formGroup: css`
    display: flex;
    flex-direction: column;
    gap: ${tokens.dimension['space-6']};
  `,
  headingContainer: css`
    display: flex;
    flex-direction: column;
  `,
  titleContainer: css`
    display: flex;
    gap: ${tokens.dimension['space-4']};
    align-items: center;
    justify-content: space-between;
  `,

  form: css`
    display: grid;
    grid-template-columns: 1fr;
    gap: ${tokens.dimension['space-6']};
    align-items: flex-start;

    @container main-container (min-width: ${BREAKPOINTS.layout.md}px) {
      grid-template-columns: 1fr 1fr;
    }
  `,

  bonusProgram: css`
    grid-column: 1 / -1;

    & input[type='checkbox'] {
      flex-shrink: 0;
    }
  `,

  bonusInputContainer: css`
    grid-column: 1 / -1;
    margin-top: ${tokens.dimension['space-5']};
    display: flex;
    flex-direction: column;
    gap: ${tokens.dimension['space-6']};

    & > div {
      flex: 1;
    }

    @container main-container (min-width: ${BREAKPOINTS.layout.md}px) {
      flex-direction: row;
      justify-content: space-between;
    }
  `,
};

type Props = {
  title: string;
  traveler: Traveler;
  errors?: PassengerErrors;
  travelDate: string;
  isSecureFlight: boolean;
  bonusPrograms: Option[];
  savedTravelers: SavedTraveler[];
  isLoggedInAndHasEuroBonus: boolean;
  updateTraveler: (id: string, traveler: Partial<Traveler>) => void;
  setPassengerFieldError: (id: string, key: PassengerKey, error: PassengerFieldError | undefined) => void;
  cepId?: CepId;
};

export const TravelerForm = forwardRef<HTMLDivElement, Props>(function TravelerForm(
  {
    title,
    traveler,
    errors,
    travelDate,
    isSecureFlight,
    savedTravelers,
    isLoggedInAndHasEuroBonus,
    updateTraveler,
    setPassengerFieldError,
    bonusPrograms,
    cepId,
  },
  ref
) {
  const {
    id,
    firstName,
    lastName,
    email,
    isPrimary,
    joinEurobonusProgram,
    frequentTraveler,
    type,
    dateOfBirth,
    gender,
  } = traveler;
  let { phoneNumber } = traveler;

  if (phoneNumber && !phoneNumber.startsWith('+')) phoneNumber = formatPhoneNumber(phoneNumber);

  const isInfant = type === TRAVELER_TYPE.INFANT;

  const [showBonusForm, setShowBonusForm] = useState(() => {
    return Boolean(isLoggedInAndHasEuroBonus) || Boolean(frequentTraveler) || Boolean(errors?.['frequentTraveler']);
  });

  const showBonusSection = traveler.type !== TRAVELER_TYPE.INFANT;

  return (
    <Card>
      <FieldsetGroup ariaLabelledBy={`traveler-${id}-heading`} css={styles.formGroup}>
        <div css={styles.headingContainer}>
          <div css={styles.titleContainer}>
            <Heading as="h2" size="title-4">
              {title}
            </Heading>
            <SavedTravelers
              traveler={traveler}
              savedTravelers={savedTravelers}
              travelDate={travelDate}
              updateTraveler={updateTraveler}
            />
          </div>

          {isInfant && <InfantSeatingNotice />}
        </div>

        <Divider direction="horizontal" variant="subtle" />

        <div css={styles.form} ref={ref}>
          <FirstNameInput
            id={`passenger-field-firstName-${id}`}
            value={firstName ?? ''}
            onChange={value => {
              updateTraveler(id, { ...traveler, firstName: value });
            }}
            onValidate={() => setPassengerFieldError(id, 'firstName', getFirstNameError(firstName))}
            error={getErrorMessage(errors?.['firstName'])}
          />
          <LastNameInput
            id={`passenger-field-lastName-${id}`}
            value={lastName ?? ''}
            onChange={value => {
              updateTraveler(id, { ...traveler, lastName: value });
            }}
            onValidate={() => setPassengerFieldError(id, 'lastName', getLastNameError(lastName))}
            error={getErrorMessage(errors?.['lastName'])}
          />

          {(type !== TRAVELER_TYPE.ADULT || isSecureFlight) && (
            <DateOfBirthInput
              id={`passenger-field-dateOfBirth-${id}`}
              value={dateOfBirth}
              onChange={value => {
                updateTraveler(id, { ...traveler, dateOfBirth: value });
              }}
              onValidate={() =>
                setPassengerFieldError(id, 'dateOfBirth', getDateOfBirthError(dateOfBirth, type, travelDate))
              }
              error={getErrorMessage(errors?.['dateOfBirth'])}
            />
          )}
          {(type === TRAVELER_TYPE.ADULT || type === TRAVELER_TYPE.YOUTH || isSecureFlight) && (
            <GenderInput
              id={`passenger-field-gender-${id}`}
              value={gender ?? ''}
              onChange={value => {
                updateTraveler(id, { ...traveler, gender: value });
                setPassengerFieldError(id, 'gender', undefined);
              }}
              error={getErrorMessage(errors?.['gender'])}
            />
          )}

          {isPrimary && cepId !== CepId.UNACCOMPANIED_MINOR && (
            <>
              <PhoneInput
                id={`passenger-field-phoneNumber-${id}`}
                value={phoneNumber ?? ''}
                onChange={value => {
                  updateTraveler(id, { ...traveler, phoneNumber: value });
                  setPassengerFieldError(id, 'phoneNumber', undefined); // onBlur is not propagated by TelInput, so clear error on change
                }}
                error={getErrorMessage(errors?.['phoneNumber'])}
              />
              <EmailInput
                id={`passenger-field-email-${id}`}
                value={email ?? ''}
                onChange={value => {
                  updateTraveler(id, { ...traveler, email: value });
                }}
                onValidate={() => setPassengerFieldError(id, 'email', getEmailError(email))}
                error={getErrorMessage(errors?.['email'])}
              />
              {!isLoggedInAndHasEuroBonus && (
                <JoinEurobonusInput
                  value={joinEurobonusProgram ?? false}
                  onChange={value => {
                    updateTraveler(id, { ...traveler, joinEurobonusProgram: value });
                  }}
                />
              )}
            </>
          )}

          {((showBonusSection && bonusPrograms) ||
            (frequentTraveler?.type && frequentTraveler?.frequentTravelerNumber)) && (
            <div css={styles.bonusProgram}>
              <Divider variant="subtle" direction="horizontal" />
              <FrequentFlyerToggle
                id={`passenger-field-frequentFlyerToggle-${id}`}
                value={showBonusForm}
                onChange={show => {
                  if (!show) {
                    updateTraveler(traveler.id, {
                      ...traveler,
                      frequentTraveler: undefined,
                    });
                  }
                  setShowBonusForm(show);
                }}
              >
                <FrequentTravelerForm
                  id={`passenger-field-frequentTraveler-${id}`}
                  css={styles.bonusInputContainer}
                  frequentTraveler={
                    frequentTraveler ?? {
                      type: '',
                      frequentTravelerNumber: '',
                      earnPoints: true,
                    }
                  }
                  updateFrequentTraveler={frequentTraveler => {
                    updateTraveler(traveler.id, { ...traveler, frequentTraveler });
                  }}
                  bonusPrograms={bonusPrograms}
                  error={getErrorMessage(errors?.['frequentTraveler'])}
                />
              </FrequentFlyerToggle>
            </div>
          )}
        </div>
      </FieldsetGroup>
    </Card>
  );
});
